import { useEffect, useMemo, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { isAxiosError } from "axios";
import { ChevronRight, Trash2, TriangleAlert, UserRound } from "lucide-react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Link, useHistory, useLocation } from "react-router-dom";
import { z } from "zod";

import client from "@/client";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { WFPButton } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Field, FieldContent, FieldError, FieldLabel } from "@/components/ui/field";
import { ServerUserProfile } from "@/constants";
import { getUserDescription, getUserInformation } from "@/utils/authentication";

const API_URL_ACCOUNT_DELETION_LINK = "/api/account_deletion/generate_link/";

const createAccountDeletionSchema = (requiredMessage: string) =>
  z.object({
    confirm: z.boolean().refine(value => value, { message: requiredMessage }),
  });

type AccountDeletionFormValues = z.infer<ReturnType<typeof createAccountDeletionSchema>>;

interface AccountDeletionLocationState {
  isExpired?: boolean;
}

interface AccountDeletionUser {
  user_profile: string;
  username: string;
  email?: string;
  full_name?: string;
}

const getRequestErrorKey = (error: unknown) => {
  const status = isAxiosError(error) ? error.response?.status : undefined;

  if (status === 429) {
    return "accountDeletion.tooManyRequests";
  }

  if (status === 400) {
    return "accountDeletion.noDeletableAccount";
  }

  return "accountDeletion.serverError";
};

export const AccountDeletion = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const { state } = useLocation<AccountDeletionLocationState | undefined>();
  const [serverErrorKey, setServerErrorKey] = useState(
    state?.isExpired ? "accountDeletion.expiredLink" : ""
  );
  const [user, setUser] = useState<AccountDeletionUser | null>(null);

  useEffect(() => {
    getUserInformation()
      .then((data: AccountDeletionUser) => setUser(data))
      .catch(() => setServerErrorKey("accountDeletion.serverError"));
  }, []);

  const schema = useMemo(
    () => createAccountDeletionSchema(t("traderDetails.form.mandatoryFieldBlankError")),
    [t]
  );

  const {
    control,
    handleSubmit,
    formState: { isSubmitting },
  } = useForm<AccountDeletionFormValues>({
    defaultValues: { confirm: false },
    mode: "onSubmit",
    reValidateMode: "onChange",
    resolver: zodResolver(schema),
  });

  const onSubmit = async () => {
    try {
      await client.post(API_URL_ACCOUNT_DELETION_LINK);
      history.push("/accountDeletion/confirmation");
    } catch (error) {
      setServerErrorKey(getRequestErrorKey(error));
    }
  };

  const isGuest = user?.user_profile === ServerUserProfile.GUEST;

  return (
    <div className="mx-auto my-8 w-full max-w-3xl px-4">
      {serverErrorKey && (
        <Alert variant="destructive" className="mb-4">
          <TriangleAlert />
          <AlertDescription>{t(serverErrorKey)}</AlertDescription>
        </Alert>
      )}

      <Card>
        <CardContent className="space-y-6">
          <h1 className="text-center font-bold text-2xl">{t("accountDeletion.title")}</h1>

          <div className="flex items-center justify-center gap-2 text-primary" aria-hidden="true">
            <UserRound className="size-12" />
            <ChevronRight className="size-7" />
            <Trash2 className="size-12" />
          </div>

          {user && (
            <p className="text-center">
              {t("accountDeletion.signedInAs", {
                name: getUserDescription(user),
                email: user.email || user.username,
              })}{" "}
              <Link to="/logout" className="text-primary underline underline-offset-4">
                {t("accountDeletion.notYou")}
              </Link>
            </p>
          )}

          {isGuest ? (
            <Alert>
              <TriangleAlert />
              <AlertDescription>{t("accountDeletion.noDeletableAccount")}</AlertDescription>
            </Alert>
          ) : (
            <>
              <div className="space-y-2">
                <p className="font-bold">{t("accountDeletion.confirmationTitle")}</p>
                <p>{t("accountDeletion.confirmationText")}</p>
              </div>

              <form noValidate className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
                <div className="space-y-2">
                  <p className="font-bold">
                    {t("accountDeletion.accountDeletionDescriptionTitle")}
                  </p>
                  <p>{t("accountDeletion.accountDeletionDescription1")}</p>
                  <p>{t("accountDeletion.accountDeletionDescription2")}</p>
                  <p>{t("accountDeletion.accountDeletionDescription3")}</p>
                </div>

                <Controller
                  control={control}
                  name="confirm"
                  render={({ field, fieldState }) => (
                    <Field orientation="horizontal">
                      <Checkbox
                        id="confirm"
                        checked={field.value}
                        onCheckedChange={checked => field.onChange(checked === true)}
                        onBlur={field.onBlur}
                        ref={field.ref}
                        disabled={isSubmitting || !user}
                        aria-invalid={!!fieldState.error}
                        aria-describedby={fieldState.error ? "confirm-error" : undefined}
                      />
                      <FieldContent>
                        <FieldLabel htmlFor="confirm" className="font-normal">
                          {t("accountDeletion.form.confirmationCheckbox")}
                          <a
                            target="_blank"
                            rel="noreferrer"
                            href="https://www.wfp.org/privacy-policy"
                            // the label forwards clicks to the checkbox; the link must not toggle it
                            onClick={event => event.stopPropagation()}
                            className="text-primary underline underline-offset-4"
                          >
                            {t("accountDeletion.form.privacyPolicy")}
                          </a>
                        </FieldLabel>
                        <FieldError id="confirm-error">{fieldState.error?.message}</FieldError>
                      </FieldContent>
                    </Field>
                  )}
                />

                <div className="flex flex-col items-center gap-4">
                  <WFPButton type="submit" variant="destructive" disabled={isSubmitting || !user}>
                    {t("accountDeletion.form.submitButtonText")}
                  </WFPButton>
                </div>
              </form>
            </>
          )}

          <div className="flex justify-center">
            <a
              href="mailto:global.farm2go@wfp.org"
              className="text-primary underline underline-offset-4"
            >
              {t("accountDeletion.form.contactUs")}
            </a>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
